import React from 'react';
import { ArrowRight, CheckCircle2, Circle } from 'lucide-react';

export type FlowStepId = 1 | 2 | 3 | 4;

export interface FlowStepState {
    id: FlowStepId;
    label: string;
    short: string;
    done: boolean;
    active: boolean;
}

interface InstagramFlowCoachProps {
    steps: FlowStepState[];
    nextMessage: string;
    onJump?: (id: FlowStepId) => void;
}

/** Always-visible checklist: where you are and what to do next. */
export const InstagramFlowCoach: React.FC<InstagramFlowCoachProps> = ({
    steps,
    nextMessage,
    onJump,
}) => {
    return (
        <section
            className="rounded-lg border border-border bg-card p-4 space-y-3"
            data-tour="instagram-flow-coach"
            aria-label="Investigation flow"
        >
            <div className="text-xs font-medium text-muted-foreground">
                Next steps
            </div>

            <ol className="flex flex-col sm:flex-row sm:items-stretch gap-2 sm:gap-0">
                {steps.map((s, i) => (
                    <li key={s.id} className="flex items-center flex-1 min-w-0">
                        <button
                            type="button"
                            onClick={() => onJump?.(s.id)}
                            className={[
                                'w-full text-left rounded-md border px-3 py-2 transition-colors',
                                s.active
                                    ? 'border-primary bg-primary/10'
                                    : s.done
                                        ? 'border-green-500/40 bg-green-500/10'
                                        : 'border-border bg-background',
                            ].join(' ')}
                            title={s.short}
                        >
                            <div className="flex items-center gap-1.5 text-[11px] font-semibold">
                                {s.done ? (
                                    <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0" />
                                ) : (
                                    <Circle className={`w-3.5 h-3.5 shrink-0 ${s.active ? 'text-primary' : 'text-muted-foreground'}`} />
                                )}
                                <span className={s.active ? 'text-foreground' : 'text-muted-foreground'}>
                                    {s.id}. {s.label}
                                </span>
                            </div>
                            <p className="mt-0.5 text-[11px] text-muted-foreground leading-snug pl-5">
                                {s.short}
                            </p>
                        </button>
                        {i < steps.length - 1 && (
                            <ArrowRight className="hidden sm:block w-4 h-4 mx-1 text-muted-foreground/50 shrink-0" aria-hidden />
                        )}
                    </li>
                ))}
            </ol>

            <div className="flex items-start gap-2 rounded-md bg-secondary/40 border border-border px-3 py-2.5 text-sm">
                <span className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mt-0.5 shrink-0">
                    Do now
                </span>
                <p className="text-foreground/90 leading-snug">{nextMessage}</p>
            </div>
        </section>
    );
};

export default InstagramFlowCoach;
